import { Box, createColumnHelper, Flex, Link, Text } from 'ui'
import { Location, Position } from 'typings/common_defs'
import { AddressString } from 'components/AddressString'
import { EditLocationButton } from './EditLocationButton'
import { EditPositionButton } from './EditPositionButton'
import { TruncatedDescription } from './TruncatedDescription'

const locationColumnHelper = createColumnHelper<Location>()

const ActionLocationButtons = ({ location }: { location: Location }) => {
  const hasTutorials = location?.tutorials?.length ?? 0 > 0

  return (
    <Flex flexWrap={['wrap', 'nowrap']} alignItems={'baseline'}>
      <Box mr={3}>
        <Link
          to={`/location/${location.id}/instructions`}
          style={{ whiteSpace: 'nowrap' }}
        >
          {hasTutorials ? 'Edit Tutorials' : 'Add Tutorials'}
        </Link>
      </Box>
      <EditLocationButton locationId={Number(location.id)} />
    </Flex>
  )
}

export const createLocationColumns = (nameColSize?: number) => [
  locationColumnHelper.accessor('name', {
    header: 'Name',
    size: nameColSize,
    cell: (info) => (
      <>
        <Box pr={3} display={['none', 'none', 'block']}>
          <Text maxLines={2}>{info.getValue()}</Text>
        </Box>
        <Box display={['block', 'block', 'none']} py={2}>
          <Text maxLines={2} fontWeight={2}>
            {info.getValue()}
          </Text>
          <Text>
            {info.row.original.address ? (
              <AddressString address={info.row.original.address} />
            ) : null}
          </Text>
          <Box mt={2}>
            <EditLocationButton locationId={Number(info.row.original.id)} />
          </Box>
        </Box>
      </>
    ),
  }),
  locationColumnHelper.accessor((location) => location.address, {
    id: 'address',
    header: 'Address',
    cell: (info) => {
      const address = info.getValue()

      return address ? <AddressString address={address} /> : null
    },
  }),
  locationColumnHelper.accessor('id', {
    header: '',
    size: 120,
    cell: (info) => <ActionLocationButtons location={info.row.original} />,
  }),
]

const positionColumnHelper = createColumnHelper<Position>()

const ActionPositionButtons = ({ position }: { position: Position }) => {
  const hasTutorials = position?.tutorials?.length ?? 0 > 0
  return (
    <Flex flexWrap={['wrap', 'nowrap']} alignItems={'baseline'}>
      <Box mr={3}>
        <Link
          to={`/position/${position.id}/instructions`}
          style={{ whiteSpace: 'nowrap' }}
        >
          {hasTutorials ? 'Edit Tutorials' : 'Add Tutorials'}
        </Link>
      </Box>
      <EditPositionButton positionId={Number(position.id)} />
    </Flex>
  )
}

export const createPositionColumns = (nameColSize?: number) => [
  positionColumnHelper.accessor('name', {
    header: 'Name',
    size: nameColSize,
    cell: (info) => (
      <>
        <Box pr={3} display={['none', 'none', 'block']}>
          <Text maxLines={2}>{info.getValue()}</Text>
        </Box>
        <Box display={['block', 'block', 'none']} py={2}>
          <Text maxLines={2} fontWeight={2}>
            {info.getValue()}
          </Text>
          <TruncatedDescription
            maxLength={140}
            content={info.row.original.about ?? ''}
          />
          <Box mt={2}>
            <ActionPositionButtons position={info.row.original} />
          </Box>
        </Box>
      </>
    ),
  }),
  positionColumnHelper.accessor('about', {
    header: 'Description',
    cell: (info) => (
      <TruncatedDescription content={info.getValue() ?? ''} maxLength={200} />
    ),
  }),
  positionColumnHelper.accessor('id', {
    header: '',
    size: 120,
    cell: (info) => <ActionPositionButtons position={info.row.original} />,
  }),
]
